import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconAlertTriangle, IconLoader2 } from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../utils/bnFormat';
import {
    FOOTER_ROLE,
    HEADER_ROLE,
    LINE_NUMBER_ROLE,
    OBJ,
    PAGE_BORDER_ROLE,
    PAGE_NUMBER_ROLE,
    TOOLS,
    WATERMARK_ROLE,
    ZOOM_STEPS,
} from './constants';
import { loadPdfJsDoc, readPageGeometry, readTextLines, searchPage } from './engine/pdfEngine';
import {
    detectWatermarks,
    removeWatermarks,
} from '../../pages/user/tools/pdf/watermark/watermarkRemoveEngine';
import { buildEditedPdf, printPdfBlob } from './engine/exportPdf';
import { fitImageToBox } from './engine/imageSwap';
import { ensureEditorFonts, measuredTextWidth } from './engine/drawObjects';
import { makeImage, makeImageSwap, makeText, newId } from './engine/objects';
import { splitIntoColumns } from './engine/insertables';
import { auditDocument } from './engine/accessibility';
import {
    NOTE_KIND,
    createNote,
    findNoteBody,
    layoutNotes,
} from './engine/footnotes';
import {
    applyDropCap,
    buildChart,
    buildCoverPage,
    buildLineNumbers,
    fieldValue,
} from './engine/insertables';
import {
    applyToEveryPage,
    buildPageBorder,
    buildRunningText,
    buildTable,
    buildImageWatermark,
    buildWatermark,
    clearRole,
    hasRole,
} from './engine/documentStamps';
import {
    boxRangeParagraph,
    docClipboardFor,
    docClipboardText,
    docFlowList,
    docSelectionParagraphs,
    rememberDocClipboard,
    tableBlock,
    usePdfEditorStore,
} from './state/usePdfEditorStore';
import { paragraphsToHtml } from './engine/docHtml';
import { DocActions, DocTitle } from './toolbar/TopBar';
import Ribbon from './toolbar/Ribbon';
import InspectorPanel from './panels/InspectorPanel';
import ThumbnailsPanel from './panels/ThumbnailsPanel';
import ReviewingPanel from './panels/ReviewingPanel';
import AccessibilityPanel from './panels/AccessibilityPanel';
import BookmarksPanel from './panels/BookmarksPanel';
import PagesModal from './panels/PagesModal';
import SignModal from './panels/SignModal';
import EmojiModal from './panels/EmojiModal';
import SymbolModal from './panels/SymbolModal';
import EquationModal from './panels/EquationModal';
import LinkModal from './panels/LinkModal';
import SearchBar from './panels/SearchBar';
import StatusBar from './panels/StatusBar';
import FloatingPageBar from './panels/FloatingPageBar';
import UploadPrompt from './panels/UploadPrompt';
import Viewport from './canvas/Viewport';
import OutlineView from './canvas/OutlineView';
import { refocusTextEditor, useCompactLayout } from './ui/prims';

/**
 * Full-screen PDF editor.
 *
 * Everything runs in the browser: pdf.js renders and reads the document,
 * annotations live in a per-page object model, and pdf-lib rebuilds the file on
 * export. Nothing is uploaded.
 *
 * @param {{
 *   file: File|Blob,
 *   open: boolean,
 *   onClose: () => void,
 *   onSave?: (blob: Blob, name: string) => Promise<void> | void,
 *   initialObjects?: object[],  // editable objects to place when the file opens (`page` = 0-based page index, default 0)
 *   initialDirty?: boolean,     // treat those objects as unsaved work (close asks first)
 *   clickToEditText?: boolean,  // a click on any page text edits it (no Edit Text tool to arm)
 *   fileName?: string,          // name to show when `file` is a bare Blob (no `.name` of its own)
 *   onPickFile?: (files: FileList) => void, // with no `file`, show an upload card that hands picks here
 *   onMinimize?: (edited: Promise<Blob>|null) => void, // shows a minimise button; the caller then passes
 *                               // `minimized`. `edited` builds the PDF as it now stands (null: nothing changed)
 *   minimized?: boolean,        // hidden but still mounted, so the document and every edit survive
 * }} props
 */
/** #rgb / #rrggbb whose every channel is very dark (the off-black the document generators use). */
function isNearBlack(color) {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color || '').trim());
    if (!m) return false;
    const hex = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
    return [0, 2, 4].every((i) => parseInt(hex.slice(i, i + 2), 16) <= 0x50);
}

export default function PdfEditorModal({ file, fileName = '', open, onClose, onSave, initialObjects = null, initialDirty = false, documentMode = false, clickToEditText = false, externalLoading = false, pageMargins = null, onPickFile = null, onMinimize = null, minimized = false }) {
    const { t } = useTranslation();
    const compact = useCompactLayout();
    const imageInputRef = useRef(null);
    // Opened straight from the tools list: the editor is up, the document is not.
    const awaitingFile = !file && !!onPickFile;

    // Granular selectors on purpose: a whole-store subscription would re-render
    // the whole shell on every pointer move of a drag.
    const ready = usePdfEditorStore((s) => s.ready);
    const loading = usePdfEditorStore((s) => s.loading);
    const error = usePdfEditorStore((s) => s.error);
    const pages = usePdfEditorStore((s) => s.pages);
    const pdf = usePdfEditorStore((s) => s.pdf);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const showThumbnails = usePdfEditorStore((s) => s.showThumbnails);
    const showInspector = usePdfEditorStore((s) => s.showInspector);
    const showSearch = usePdfEditorStore((s) => s.showSearch);
    const pagesModal = usePdfEditorStore((s) => s.pagesModal);
    const signModal = usePdfEditorStore((s) => s.signModal);
    const emojiModal = usePdfEditorStore((s) => s.emojiModal);
    const symbolModal = usePdfEditorStore((s) => s.symbolModal);
    const equationModal = usePdfEditorStore((s) => s.equationModal);
    const linkDraft = usePdfEditorStore((s) => s.linkDraft);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const setLinkDraft = usePdfEditorStore((s) => s.setLinkDraft);
    const setSearchOpen = usePdfEditorStore((s) => s.setSearchOpen);
    const setPagesModal = usePdfEditorStore((s) => s.setPagesModal);
    const setSignModal = usePdfEditorStore((s) => s.setSignModal);
    const setEmojiModal = usePdfEditorStore((s) => s.setEmojiModal);
    const setSymbolModal = usePdfEditorStore((s) => s.setSymbolModal);
    const setEquationModal = usePdfEditorStore((s) => s.setEquationModal);
    const showBookmarks = usePdfEditorStore((s) => s.showBookmarks);
    const showReviewing = usePdfEditorStore((s) => s.showReviewing);
    const showAccessibility = usePdfEditorStore((s) => s.showAccessibility);
    const displayMode = usePdfEditorStore((s) => s.displayMode);
    const splitView = usePdfEditorStore((s) => s.splitView);
    const splitRatio = usePdfEditorStore((s) => s.splitRatio);

    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    // How far a long job has got. Compare walks every page against another
    // file, and the index walks every page for every term; both take long
    // enough that a count is the difference between "working" and "hung".
    const [taskProgress, setTaskProgress] = useState(null);
    const [saving, setSaving] = useState(false);
    const [removingWatermark, setRemovingWatermark] = useState(false);
    const [scale, setScale] = useState(1);
    const [hits, setHits] = useState([]);
    const [hitIndex, setHitIndex] = useState(0);
    const [searchRunning, setSearchRunning] = useState(false);
    const [query, setQuery] = useState('');

    /* ------------------------------------------------------------- loading */

    useEffect(() => {
        if (!open || !file) return undefined;
        let cancelled = false;

        const load = async () => {
            const store = usePdfEditorStore.getState();
            store.setLoading(true);
            try {
                await ensureEditorFonts();
                const bytes = await file.arrayBuffer();
                const doc = await loadPdfJsDoc(bytes);
                const geometry = await readPageGeometry(doc);
                if (cancelled) return;
                // Document mode (AI documents): click-to-edit text, no box around it, and the PDF-only
                // "edit text" tool hidden. Set before the pages mount, and on every open so a
                // non-document consumer resets it to false.
                usePdfEditorStore.setState({
                    documentMode: !!documentMode,
                    docMargins: documentMode ? pageMargins : null,
                    clickToEditText: !!clickToEditText,
                });
                store.loadDocument({
                    fileName: file.name || fileName || 'document.pdf',
                    sourceBytes: bytes,
                    pdf: doc,
                    pages: geometry.map((g, i) => ({
                        uid: newId('pg'),
                        sourceIndex: i,
                        width: g.width,
                        height: g.height,
                        baseRotation: g.rotation,
                        userRotation: 0,
                    })),
                });
                if (Array.isArray(initialObjects) && initialObjects.length) {
                    // `page` (0-based, default 0) says which page an object belongs to.
                    const live = usePdfEditorStore.getState().pages;
                    const byUid = {};
                    for (const { page = 0, ...obj } of initialObjects) {
                        const target = live[page] || live[0];
                        // A document's body text is plain black — the soft near-black the generators
                        // use reads as grey on a screen. Colours the user picked are left alone.
                        if (documentMode && (obj.type === OBJ.TEXT || obj.type === OBJ.TABLE)) {
                            if (isNearBlack(obj.color)) obj.color = '#000000';
                            if (obj.type === OBJ.TABLE && isNearBlack(obj.borderColor)) obj.borderColor = '#000000';
                        }
                        if (target) (byUid[target.uid] ||= []).push(obj);
                    }
                    usePdfEditorStore.setState({ objectsByUid: byUid, dirty: !!initialDirty });
                }
                // The file's own name first; a bare Blob (an edited copy coming
                // back in) has none, so then the caller's name, then the PDF's
                // own /Title — "document" only when all three are missing.
                const metaTitle = file.name || fileName
                    ? ''
                    : await doc.getMetadata().then((m) => String(m?.info?.Title || '').trim()).catch(() => '');
                if (cancelled) return;
                setTitle((file.name || fileName || metaTitle || 'document.pdf').replace(/\.pdf$/i, ''));
            } catch (err) {
                if (cancelled) return;
                usePdfEditorStore.getState().setError(err?.message === 'password'
                    ? t('pdfEditor.errors.password')
                    : t('pdfEditor.errors.load'));
            }
        };

        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, file, documentMode]);

    useEffect(() => () => usePdfEditorStore.getState().resetDocument(), []);

    // Phone defaults, applied when the layout actually becomes compact rather
    // than from `innerWidth` at module load — which is 0 in some embeds, and
    // would then hide the thumbnails on a desktop too. Only on the transition,
    // so re-opening the strip by hand sticks.
    const autoHidThumbs = useRef(false);
    useEffect(() => {
        const store = usePdfEditorStore.getState();
        if (compact) {
            store.setFitMode('width');
            if (store.showThumbnails) {
                store.toggleThumbnails();
                autoHidThumbs.current = true;
            }
        } else if (autoHidThumbs.current) {
            autoHidThumbs.current = false;
            if (!store.showThumbnails) store.toggleThumbnails();
        }
    }, [compact]);

    // Lock the page behind the overlay — not while minimised, when that page
    // is what the user is looking at.
    useEffect(() => {
        if (!open || minimized) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [open, minimized]);

    /* -------------------------------------------------------------- search */

    useEffect(() => {
        if (!showSearch || !query.trim() || !pdf) {
            setHits([]);
            setHitIndex(0);
            return undefined;
        }
        let cancelled = false;
        setSearchRunning(true);

        const run = async () => {
            const found = [];
            for (let i = 0; i < pages.length; i += 1) {
                const page = pages[i];
                if (page.sourceIndex == null) continue;
                // eslint-disable-next-line no-await-in-loop
                const pageHits = await searchPage(pdf, page.sourceIndex + 1, query.trim());
                if (cancelled) return;
                found.push(...pageHits.map((hit, n) => ({
                    ...hit,
                    id: `${page.uid}-${hit.lineId}-${n}`,
                    pageIndex: i,
                })));
            }
            if (cancelled) return;
            setHits(found);
            setHitIndex(0);
            setSearchRunning(false);
        };

        const timer = setTimeout(run, 240);
        return () => {
            cancelled = true;
            clearTimeout(timer);
            setSearchRunning(false);
        };
    }, [query, showSearch, pdf, pages]);

    const hitsByPage = useMemo(() => {
        const map = {};
        for (const hit of hits) {
            (map[hit.pageIndex] ||= []).push(hit);
        }
        return map;
    }, [hits]);

    const stepHit = useCallback((delta) => {
        if (!hits.length) return;
        const next = (hitIndex + delta + hits.length) % hits.length;
        setHitIndex(next);
        usePdfEditorStore.getState().goToPage(hits[next].pageIndex);
    }, [hits, hitIndex]);

    /* ------------------------------------------------------------ inserts */

    const activePage = pages[currentPage];

    const placeImage = useCallback((src, naturalW, naturalH, kind = OBJ.IMAGE) => {
        if (!activePage) return;
        const maxW = activePage.width * (kind === 'sign' ? 0.32 : 0.5);
        const width = Math.min(maxW, naturalW * 0.75);
        const height = (naturalH / naturalW) * width;
        const object = makeImage(
            {
                x: (activePage.width - width) / 2,
                y: Math.min(activePage.height - height - 24, activePage.height * 0.55),
                w: width,
                h: height,
            },
            src,
            naturalW,
            naturalH,
            kind,
        );
        usePdfEditorStore.getState().addObject(activePage.uid, object);
        usePdfEditorStore.getState().setTool(TOOLS.SELECT);
    }, [activePage]);

    const handleImageFile = useCallback(async (imageFile) => {
        if (!imageFile) return;
        try {
            const src = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(imageFile);
            });
            const img = await new Promise((resolve, reject) => {
                const el = new Image();
                el.onload = () => resolve(el);
                el.onerror = reject;
                el.src = src;
            });
            placeImage(src, img.naturalWidth, img.naturalHeight);
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.errors.image'),
                color: 'red',
            });
        }
    }, [placeImage, t]);

    /**
     * Page numbers as a SWITCH, not a stamp.
     *
     * Clicking the tool twice used to number every page twice, and taking that
     * back meant deleting one text box per page. The objects it creates carry a
     * role, so a second click is simply "off" — and choosing the other numerals
     * re-numbers in place rather than adding a second set.
     */
    const togglePageNumbers = useCallback((digits = null) => {
        const store = usePdfEditorStore.getState();
        const style = digits || store.toolSettings.pageNumbers?.digits || 'bn';
        const numbered = Object.values(store.objectsByUid)
            .some((list) => list?.some((o) => o.role === PAGE_NUMBER_ROLE));
        // Same numerals as what is already there = the user is switching it off.
        const off = numbered && (!digits || digits === store.toolSettings.pageNumbers?.digits);

        if (digits) store.updateToolSettings('pageNumbers', { digits });

        const settings = { ...store.toolSettings.text, fontSize: 10, align: 'center' };
        store.commit((state) => {
            const next = {};
            state.pages.forEach((page, index) => {
                // Whatever this switch put there last time goes first, so the
                // numbers can never pile up.
                const kept = (state.objectsByUid[page.uid] || [])
                    .filter((o) => o.role !== PAGE_NUMBER_ROLE);
                if (off) { next[page.uid] = kept; return; }
                const label = style === 'en'
                    ? String(index + 1)
                    : toBnDigits(index + 1);
                next[page.uid] = [...kept, {
                    ...makeText(
                        { x: page.width / 2 - 40, y: page.height - 34, w: 80, h: 16 },
                        settings,
                        label,
                    ),
                    role: PAGE_NUMBER_ROLE,
                }];
            });
            return { objectsByUid: { ...state.objectsByUid, ...next } };
        });
    }, []);

    /**
     * The rest of the document-wide switches, all built the same way as page
     * numbers: on turns the role into objects on every page, off filters that
     * role back out. `text` of null means "off"; a string turns it on.
     */
    const applyDocumentStamp = useCallback((role, build, { atBack = false } = {}) => {
        const store = usePdfEditorStore.getState();
        store.commit((state) => (build
            ? applyToEveryPage(state, role, build, { atBack })
            : clearRole(state, role)));
    }, []);

    const toggleWatermark = useCallback((text) => {
        const store = usePdfEditorStore.getState();
        const on = hasRole(store.objectsByUid, WATERMARK_ROLE);
        if (on && !text) { applyDocumentStamp(WATERMARK_ROLE, null); return; }
        const label = (text || '').trim();
        if (!label) return;
        const settings = store.toolSettings.text;
        applyDocumentStamp(WATERMARK_ROLE, (page) => buildWatermark(page, label, settings), { atBack: true });
    }, [applyDocumentStamp]);

    /**
     * A logo as the watermark.
     *
     * Read once, here, rather than per page: the same data URL goes on to all
     * twenty-five, and decoding it twenty-five times would be twenty-four
     * decodes for nothing. `createImageBitmap` rather than `<img>.decode()`,
     * which never settles while the tab is unpainted.
     */
    const watermarkImageRef = useRef(null);

    const applyWatermarkImage = useCallback(async (picked) => {
        if (!picked) return;
        if (!picked.type?.startsWith('image/')) {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.errors.image'),
                color: 'red',
            });
            return;
        }
        try {
            const src = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(picked);
            });
            const bitmap = await createImageBitmap(picked);
            applyDocumentStamp(
                WATERMARK_ROLE,
                (page) => buildImageWatermark(page, src, bitmap.width, bitmap.height),
                { atBack: true },
            );
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.errors.image'),
                color: 'red',
            });
        }
    }, [applyDocumentStamp, t]);

    const toggleRunningText = useCallback((where, text) => {
        const role = where === 'header' ? HEADER_ROLE : FOOTER_ROLE;
        const store = usePdfEditorStore.getState();
        const on = hasRole(store.objectsByUid, role);
        if (on && !text) { applyDocumentStamp(role, null); return; }
        const label = (text || '').trim();
        if (!label) return;
        const settings = store.toolSettings.text;
        applyDocumentStamp(role, (page) => buildRunningText(page, label, settings, where, store.margins));
    }, [applyDocumentStamp]);

    const togglePageBorder = useCallback((stroke) => {
        const store = usePdfEditorStore.getState();
        const on = hasRole(store.objectsByUid, PAGE_BORDER_ROLE);
        if (on && !stroke) { applyDocumentStamp(PAGE_BORDER_ROLE, null); return; }
        if (!stroke) return;
        applyDocumentStamp(PAGE_BORDER_ROLE, (page) => buildPageBorder(page, stroke));
    }, [applyDocumentStamp]);

    /** A table on the page the user is looking at, as the lines that draw it. */
    const insertTable = useCallback((rows, cols) => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        if (!page) return;
        const [table] = buildTable(page, rows, cols, store.toolSettings.shape);
        if (store.documentMode) {
            // Document mode: drop the table into the text flow — left-aligned with the
            // content, below the last block — then re-flow so nothing overlaps.
            const flows = (store.objectsByUid[page.uid] || []).filter((o) => !o.role);
            const left = flows.length ? Math.min(...flows.map((o) => o.x)) : 56;
            const bottom = flows.length ? Math.max(...flows.map((o) => o.y + (o.h || 0))) : 56;
            table.x = left;
            table.y = bottom + 10;
            store.addObject(page.uid, table);
            store.reflowDocument();
        } else {
            store.addObject(page.uid, table);
        }
    }, []);

    /** Word's Insert → Cover Page, as a blank first page with the title set on it. */
    const insertCoverPage = useCallback((details) => {
        const store = usePdfEditorStore.getState();
        store.insertBlankPage(-1);
        // The blank page lands at the front; the title goes onto it once the
        // store has it, not before.
        const after = usePdfEditorStore.getState();
        const page = after.pages[0];
        if (!page) return;
        after.addObjects(page.uid, buildCoverPage(page, details, after.toolSettings.text));
        after.goToPage(0);
    }, []);

    const insertChart = useCallback((spec) => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        if (!page) return;
        store.addObject(page.uid, buildChart(page, spec));
    }, []);

    /**
     * Word's Insert → Screenshot, as the browser can do it: the operating
     * system's own picker, one frame grabbed off the stream, stream stopped.
     */
    const insertScreenshot = useCallback(async () => {
        if (!navigator.mediaDevices?.getDisplayMedia) {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.errors.noScreenCapture'),
                color: 'red',
            });
            return;
        }
        let stream;
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            const video = document.createElement('video');
            video.srcObject = stream;
            await video.play();
            // One frame is enough, but the first is often still black.
            await new Promise((resolve) => { requestAnimationFrame(() => requestAnimationFrame(resolve)); });
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            canvas.getContext('2d').drawImage(video, 0, 0);
            placeImage(canvas.toDataURL('image/png'), canvas.width, canvas.height);
        } catch (err) {
            // A cancelled picker is not a failure; anything else is.
            if (err?.name !== 'NotAllowedError' && err?.name !== 'AbortError') {
                notifications.show({
                    title: t('common.error'),
                    message: t('pdfEditor.errors.screenCaptureFailed'),
                    color: 'red',
                });
            }
        } finally {
            stream?.getTracks().forEach((track) => track.stop());
        }
    }, [placeImage, t]);

    /** A field: the value is read once and typed onto the page as ordinary text. */
    const insertField = useCallback((kind) => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        if (!page) return;
        const value = fieldValue(kind, {
            fileName: file?.name || '',
            pageCount: store.pages.length,
            pageIndex: store.currentPage,
        });
        if (!value) return;
        const settings = { ...store.toolSettings.text, fontSize: 11 };
        const probe = makeText({ x: 0, y: 0, w: 400, h: 20 }, settings, value);
        const width = Math.min(300, measuredTextWidth(probe) + 10);
        store.addObject(page.uid, makeText(
            {
                x: (page.width - width) / 2,
                y: page.height * 0.5,
                w: width,
                h: settings.fontSize * settings.lineHeight + 6,
            },
            settings,
            value,
        ));
    }, [file]);

    const applyParagraphSpacing = useCallback((lineHeight) => {
        const store = usePdfEditorStore.getState();
        store.updateToolSettings('text', { lineHeight });
        store.commit((state) => {
            const next = {};
            state.pages.forEach((page) => {
                next[page.uid] = (state.objectsByUid[page.uid] || []).map((obj) => (
                    obj.type === OBJ.TEXT && !obj.role ? { ...obj, lineHeight } : obj
                ));
            });
            return { objectsByUid: { ...state.objectsByUid, ...next } };
        });
    }, []);

    /** Drop cap on the selected text box. */
    const toggleDropCap = useCallback(() => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        const object = (store.objectsByUid[page?.uid] || []).find((o) => o.id === store.selectedId);
        if (!object || object.type !== OBJ.TEXT) return;
        const capped = (object.marks || []).some((m) => m.start === 0 && m.end === 1 && m.style?.fontSize);
        const patch = applyDropCap(object, !capped);
        if (patch) store.updateObject(page.uid, object.id, patch);
    }, []);

    /**
     * Line numbers down the margin, taken from the page's own text layer — a
     * page of pictures has no lines to number, and gets none.
     */
    const toggleLineNumbers = useCallback(async () => {
        const store = usePdfEditorStore.getState();
        const numbered = Object.values(store.objectsByUid)
            .some((list) => list?.some((o) => o.role === LINE_NUMBER_ROLE));
        if (numbered) {
            store.commit((state) => clearRole(state, LINE_NUMBER_ROLE));
            return;
        }
        const doc = store.pdf;
        if (!doc) return;
        const perPage = new Map();
        for (const page of store.pages) {
            if (page.sourceIndex == null) continue;
            // eslint-disable-next-line no-await-in-loop
            const lines = await readTextLines(doc, page.sourceIndex + 1);
            perPage.set(page.uid, lines);
        }
        const settings = store.toolSettings.text;
        store.commit((state) => {
            const next = { ...state.objectsByUid };
            state.pages.forEach((page) => {
                const lines = perPage.get(page.uid) || [];
                const kept = (next[page.uid] || []).filter((o) => o.role !== LINE_NUMBER_ROLE);
                next[page.uid] = [
                    ...kept,
                    ...buildLineNumbers(page, lines, settings).map((o) => ({ ...o, role: LINE_NUMBER_ROLE })),
                ];
            });
            return { objectsByUid: next };
        });
    }, []);

    const insertDateStamp = useCallback(() => {
        if (!activePage) return;
        const settings = { ...usePdfEditorStore.getState().toolSettings.text, fontSize: 11 };
        const text = new Date().toLocaleDateString();
        const probe = makeText({ x: 0, y: 0, w: 400, h: 20 }, settings, text);
        const width = Math.min(240, measuredTextWidth(probe) + 8);
        usePdfEditorStore.getState().addObject(activePage.uid, makeText(
            { x: activePage.width - width - 36, y: 34, w: width, h: settings.fontSize * settings.lineHeight + 6 },
            settings,
            text,
        ));
    }, [activePage]);

    /** A special character: real text, so it stays editable and searchable. */
    const insertSymbol = useCallback((char) => {
        if (!activePage) return;
        const settings = usePdfEditorStore.getState().toolSettings.text;
        const probe = makeText({ x: 0, y: 0, w: 200, h: 20 }, settings, char);
        const width = Math.max(24, measuredTextWidth(probe) + 8);
        usePdfEditorStore.getState().addObject(activePage.uid, makeText(
            {
                x: (activePage.width - width) / 2,
                y: activePage.height * 0.45,
                w: width,
                h: settings.fontSize * settings.lineHeight + 6,
            },
            settings,
            char,
        ));
        usePdfEditorStore.getState().setTool(TOOLS.SELECT);
    }, [activePage]);

    /* -------------------------------------------------------------- export */

    const exportBlob = useCallback(async () => {
        const state = usePdfEditorStore.getState();
        // The pdf.js document goes along: the exporter re-embeds its font
        // programs so edited paragraphs stay real text in the document's own
        // typeface instead of being stamped as images.
        // A paragraph that is open but unchanged (`pending`) is not an edit:
        // the page keeps its own words, so the box must not print on top of them.
        const objectsByUid = Object.fromEntries(Object.entries(state.objectsByUid)
            .map(([uid, list]) => [uid, (list || []).filter((o) => !o.pending)]));
        return buildEditedPdf(state.sourceBytes, state.pages, objectsByUid, {
            pdf: state.pdf,
            bookmarks: state.bookmarks,
            language: state.language,
            title,
        });
    }, []);

    const outputName = `${(title || 'document').trim() || 'document'}.pdf`;

    // Minimising hands the page behind the editor the PDF as it now stands, so
    // its preview shows the edits — built only when something was changed.
    const handleMinimize = useCallback(() => {
        const state = usePdfEditorStore.getState();
        const changed = state.dirty || state.past.length > 0;
        onMinimize?.(changed ? exportBlob() : null);
    }, [onMinimize, exportBlob]);

    const runExport = useCallback(async (consume) => {
        if (busy) return;
        setBusy(true);
        try {
            const blob = await exportBlob();
            await consume(blob);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message === 'password'
                    ? t('pdfEditor.errors.password')
                    : t('pdfEditor.errors.export'),
                color: 'red',
            });
        } finally {
            setBusy(false);
        }
    }, [busy, exportBlob, t]);

    /**
     * The document-level commands, in one place.
     *
     * Every ribbon switch that changes the whole document goes through
     * `runCommand` rather than calling its handler directly, so there is one
     * list of what those switches are.
     */
    /**
     * Take the watermark the PDF arrived with out of the document.
     *
     * The editor's own watermark command adds one as an overlay object; this is
     * the other direction and works on the file body, so it rewrites the source
     * the canvas and the export both read. What comes back is a document with
     * the stamp gone rather than covered — nothing is painted over it.
     */
    const removeSourceWatermark = useCallback(async () => {
        const store = usePdfEditorStore.getState();
        if (!store.sourceBytes || removingWatermark) return;
        setRemovingWatermark(true);
        try {
            const source = new Blob([store.sourceBytes], { type: 'application/pdf' });
            const found = await detectWatermarks(source);
            if (!found.candidates.length) {
                notifications.show({
                    title: t('pdfEditor.watermarkRemove.noneTitle'),
                    message: t('pdfEditor.watermarkRemove.none'),
                    color: 'blue',
                });
                return;
            }
            const { blob, removed } = await removeWatermarks(source, found.candidates.map((c) => c.id));
            const bytes = await blob.arrayBuffer();
            const doc = await loadPdfJsDoc(bytes);
            usePdfEditorStore.getState().replaceSource({ sourceBytes: bytes, pdf: doc });
            notifications.show({
                title: t('common.success'),
                message: t('pdfEditor.watermarkRemove.done', { count: removed }),
                color: 'green',
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.watermarkRemove.failed'),
                color: 'red',
            });
        } finally {
            setRemovingWatermark(false);
        }
    }, [removingWatermark, t]);

    const commands = useMemo(() => ({
        watermark: (args) => toggleWatermark(args?.text ?? null),
        removeWatermark: () => removeSourceWatermark(),
        header: (args) => toggleRunningText('header', args?.text ?? null),
        footer: (args) => toggleRunningText('footer', args?.text ?? null),
        pageBorder: (args) => togglePageBorder(args?.stroke ?? null),
        pageNumbers: (args) => togglePageNumbers(args?.digits ?? null),
        lineNumbers: () => toggleLineNumbers(),
        margins: (args) => usePdfEditorStore.getState().setMargins(args.margins, args.preset),
        pageSize: (args) => usePdfEditorStore.getState().setPageSize(args.size, { allPages: true, landscape: !!args.landscape }),
        paragraphSpacing: (args) => applyParagraphSpacing(args.lineHeight),
        hyphenation: (args) => usePdfEditorStore.getState().setHyphenation(!!args.on),
    }), [
        toggleWatermark, toggleRunningText, togglePageBorder,
        togglePageNumbers, toggleLineNumbers, applyParagraphSpacing,
        removeSourceWatermark,
    ]);

    const runCommand = useCallback((name, args) => commands[name]?.(args), [commands]);

    /* -------------------------------------------------------- accessibility */

    const runAccessibilityCheck = useCallback(async () => {
        const store = usePdfEditorStore.getState();
        setBusy(true);
        try {
            const issues = await auditDocument({
                pages: store.pages,
                objectsByUid: store.objectsByUid,
                pdf: store.pdf,
                language: store.language,
                title,
            });
            usePdfEditorStore.getState().setAccessibilityIssues(issues);
            notifications.show({
                title: t('pdfEditor.a11y.title'),
                message: issues.length
                    ? t('pdfEditor.a11y.found', { count: issues.length })
                    : t('pdfEditor.a11y.clean'),
                color: issues.length ? 'yellow' : 'green',
            });
        } finally {
            setBusy(false);
        }
    }, [t, title]);

    /* ------------------------------------------------- columns and paper */

    /** Word's Columns, on the text box that is selected. */
    const applyColumns = useCallback((count) => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        const object = (store.objectsByUid[page?.uid] || []).find((o) => o.id === store.selectedId);
        const boxes = splitIntoColumns(object, count);
        if (!boxes) {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.columns.needsText'),
                color: 'yellow',
            });
            return;
        }
        store.commit((state) => ({
            objectsByUid: {
                ...state.objectsByUid,
                [page.uid]: [
                    ...(state.objectsByUid[page.uid] || []).filter((o) => o.id !== object.id),
                    ...boxes,
                ],
            },
            selectedId: boxes[0].id,
        }));
    }, [t]);

    /* --------------------------------------------------- replace an image */

    /**
     * Swap one of the PDF's own pictures for a file from disk.
     *
     * The click picks the picture; the file picker opens straight away, because
     * "replace this" has no second question worth asking. The object that comes
     * out draws nothing on the canvas — the preview document carries the swap,
     * the same way it carries a deleted paragraph — so what is on screen is
     * what the export writes.
     */
    const replaceTargetRef = useRef(null);
    const replaceImageRef = useRef(null);

    const startReplaceImage = useCallback((uid, placement) => {
        replaceTargetRef.current = { uid, placement };
        replaceImageRef.current?.click();
    }, []);

    const handleReplaceImageFile = useCallback(async (picked) => {
        const target = replaceTargetRef.current;
        replaceTargetRef.current = null;
        if (!picked || !target) return;

        try {
            const src = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(picked);
            });
            const { placement, uid } = target;
            // The page stretches whatever it is given across the old picture's
            // rect, so the new one is letterboxed to that shape first — without
            // it a portrait photograph replacing a banner comes out squashed.
            const fitted = await fitImageToBox(src, placement.w, placement.h);

            const store = usePdfEditorStore.getState();
            const page = store.pages.find((p) => p.uid === uid);
            const scale = page?.contentScale || 1;
            const offset = page?.contentOffset || { x: 0, y: 0 };

            store.addObject(uid, makeImageSwap(
                {
                    x: placement.x * scale + offset.x,
                    y: placement.y * scale + offset.y,
                    w: placement.w * scale,
                    h: placement.h * scale,
                },
                fitted.src,
                fitted.width,
                fitted.height,
                // Kept in the SOURCE page's space: it is how the exporter finds
                // the `Do` that paints this picture, and that never moves.
                { x: placement.x, y: placement.y, w: placement.w, h: placement.h },
            ));
            usePdfEditorStore.getState().setTool(TOOLS.SELECT);
        } catch {
            notifications.show({
                title: t('common.error'),
                message: t('pdfEditor.errors.image'),
                color: 'red',
            });
        }
    }, [t]);

    const splitDragRef = useRef(null);

    /* ------------------------------------------------------- foot / endnotes */

    /**
     * A note at the reader's place: the superscript marker beside the selected
     * text (or mid-page to be dragged), and the note itself at the foot of the
     * page — or, for an endnote, on the last page with the rest of them.
     *
     * The whole document is renumbered afterwards rather than the new note
     * simply taking the next number, because a note inserted in the middle
     * pushes every later one along.
     */
    const insertNote = useCallback((kind) => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        if (!page) return;
        const anchor = (store.objectsByUid[page.uid] || [])
            .find((o) => o.id === store.selectedId && o.type === OBJ.TEXT);
        const { marker, body } = createNote(page, anchor, store.toolSettings.text, kind);

        store.commit((state) => {
            const withNote = {
                ...state.objectsByUid,
                [page.uid]: [...(state.objectsByUid[page.uid] || []), marker, body],
            };
            return { objectsByUid: layoutNotes(state.pages, withNote) };
        });

        // Straight into typing the note: an empty note is not worth inserting.
        const placed = findNoteBody(
            usePdfEditorStore.getState().pages,
            usePdfEditorStore.getState().objectsByUid,
            marker.noteId,
        );
        if (placed) {
            usePdfEditorStore.getState().goToPage(placed.pageIndex);
            usePdfEditorStore.getState().setEditing(placed.body.id);
        }
    }, []);

    /* ------------------------------------------------------------ bookmarks */

    /**
     * A bookmark for the page you are on, named after whatever is most likely
     * to be its heading: the selected text box, else the largest line of text
     * near the top of the page, else just the page number.
     */
    const addBookmarkHere = useCallback(() => {
        const store = usePdfEditorStore.getState();
        const page = store.pages[store.currentPage];
        if (!page) return;
        const selected = (store.objectsByUid[page.uid] || [])
            .find((o) => o.id === store.selectedId && o.type === OBJ.TEXT);
        const biggest = (store.objectsByUid[page.uid] || [])
            // Stamped furniture is not a heading: the watermark is the largest
            // text on every page it is on, and "DRAFT" is nobody's chapter title.
            .filter((o) => o.type === OBJ.TEXT && !o.role && o.text?.trim())
            .sort((a, b) => (b.fontSize || 0) - (a.fontSize || 0))[0];
        const title = (selected?.text || biggest?.text || '')
            .split('\n')[0]
            .trim()
            .slice(0, 80)
            || t('pdfEditor.bookmarks.defaultTitle', { page: store.currentPage + 1 });
        store.addBookmark(title, store.currentPage, 0);
        if (!store.showBookmarks) store.toggleBookmarks();
    }, [t]);

    const handleDownload = () => runExport((blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = outputName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        usePdfEditorStore.getState().markSaved();
    });

    const handlePrint = () => runExport((blob) => printPdfBlob(blob));

    const handleSave = () => {
        if (!onSave) return;
        setSaving(true);
        runExport(async (blob) => {
            await onSave(blob, outputName);
            usePdfEditorStore.getState().markSaved();
        }).finally(() => setSaving(false));
    };

    const requestClose = useCallback(() => {
        const state = usePdfEditorStore.getState();
        // Document mode (AI documents): the page saves the edit as it closes, so closing is
        // immediate — a blocking confirm() is also dropped silently by embedded browsers, which
        // made the close button look dead.
        // eslint-disable-next-line no-alert
        if (!documentMode && state.dirty && !window.confirm(t('pdfEditor.confirmDiscard'))) {
            return;
        }
        state.setPagesModal(false);
        state.setSignModal(false);
        state.setEmojiModal(false);
        state.setSymbolModal(false);
        state.setEquationModal(false);
        state.setSearchOpen(false);
        onClose();
    }, [onClose, t, documentMode]);

    /* ------------------------------------------------- document selection */

    // Document mode (AI documents): the keys and clipboard for a selection that
    // crosses paragraphs, as Word has them. Capture phase, so Select All still
    // reaches here from inside a box being typed in (the editor stops its keys
    // from bubbling). None of this runs for the PDF tool.
    useEffect(() => {
        if (!open || minimized || !documentMode) return undefined;
        const store = () => usePdfEditorStore.getState();
        // After a replace, carry on typing where the change ended.
        const caretAt = (caret) => {
            if (!caret?.id) return;
            store().setEditing(caret.id, null, { offset: caret.offset });
        };
        // What a copy carries: plain text for anywhere, HTML so Word and
        // Google Docs keep the formatting, and the paragraphs themselves,
        // remembered for pasting back into a document here.
        const payloadOf = (paragraphs) => {
            rememberDocClipboard(store(), paragraphs);
            return { text: docClipboardText(paragraphs), html: paragraphsToHtml(paragraphs) };
        };
        // A selected table copies whole: it is painted on the canvas, so the
        // browser has nothing to copy there. A click on a table opens a cell,
        // so with only the caret in a cell (no text selected) a COPY takes the
        // whole table too — a cut there would be a surprise, so it does not.
        const selectedTable = (st, e, { inCell = false } = {}) => {
            const tag = e.target?.tagName;
            if (!st.selectedId || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return null;
            if (st.editingId && !(inCell && st.editingId === st.selectedId)) return null;
            if (!(window.getSelection()?.isCollapsed ?? true)) return null;
            for (const [uid, list] of Object.entries(st.objectsByUid)) {
                const obj = (list || []).find((o) => o.id === st.selectedId);
                if (obj) return obj.type === OBJ.TABLE ? { uid, obj } : null;
            }
            return null;
        };
        // A selection inside the one paragraph being typed in, as a paragraph.
        const editorParagraphs = (st) => {
            const { start, end } = st.textSelection;
            if (!st.editingId || end <= start) return null;
            const obj = Object.values(st.objectsByUid).flat().find((o) => o && o.id === st.editingId);
            return obj && obj.type === OBJ.TEXT ? [boxRangeParagraph(obj, start, end)] : null;
        };
        const writeClipboard = (paragraphs) => {
            const { text, html } = payloadOf(paragraphs);
            const item = typeof ClipboardItem !== 'undefined'
                ? new ClipboardItem({
                    'text/plain': new Blob([text], { type: 'text/plain' }),
                    'text/html': new Blob([html], { type: 'text/html' }),
                })
                : null;
            const write = item && navigator.clipboard?.write
                ? navigator.clipboard.write([item])
                : navigator.clipboard?.writeText(text);
            write?.catch(() => navigator.clipboard?.writeText(text).catch(() => {}));
        };
        // Paste over the selection: whole paragraphs when it is our own copy.
        const pasteOver = (st, text) => {
            const rich = docClipboardFor(text);
            if (rich && (rich.paragraphs.length > 1 || rich.paragraphs[0].table)) {
                const at = st.replaceDocRange(st.docSelection, '');
                if (at) caretAt(store().pasteDocParagraphs({ id: at.id, start: at.offset, end: at.offset }, rich.paragraphs));
                return;
            }
            caretAt(st.replaceDocRange(st.docSelection, text));
        };

        const onKey = (e) => {
            const st = store();
            if (!st.ready) return;
            const mod = e.ctrlKey || e.metaKey;
            const key = e.key.toLowerCase();

            const tag = e.target?.tagName;
            if (mod && key === 'a' && !e.shiftKey && !e.altKey && tag !== 'INPUT' && tag !== 'TEXTAREA') {
                const list = docFlowList(st);
                if (!list.length) return;
                e.preventDefault();
                e.stopPropagation();
                const last = list[list.length - 1].obj;
                st.setDocSelection({
                    anchor: { id: list[0].obj.id, offset: 0 },
                    focus: { id: last.id, offset: String(last.text ?? '').length },
                });
                return;
            }

            const sel = st.docSelection;
            if (!sel) {
                const table = mod && (key === 'c' || key === 'x') && !e.shiftKey && !e.altKey && selectedTable(st, e, { inCell: key === 'c' });
                if (!table) return;
                // The `copy` / `cut` event fills the clipboard too, as below.
                writeClipboard([tableBlock(table.obj)]);
                if (key === 'x') {
                    e.preventDefault();
                    st.removeObject(table.uid, table.obj.id);
                }
                return;
            }
            if (mod && key === 'c') {
                // The `copy` event below fills the clipboard too; this covers
                // a browser that sends none with nothing selected in the DOM.
                writeClipboard(docSelectionParagraphs(st));
                return;
            }
            if (mod && key === 'x') {
                e.preventDefault();
                writeClipboard(docSelectionParagraphs(st));
                caretAt(st.replaceDocRange(sel, ''));
                return;
            }
            if (mod || e.altKey) return; // undo, zoom and the rest keep their own handlers
            if (e.key === 'Escape') {
                e.preventDefault();
                st.setDocSelection(null);
                return;
            }
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                e.stopPropagation();
                caretAt(st.replaceDocRange(sel, ''));
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                caretAt(st.replaceDocRange(sel, '\n'));
                return;
            }
            if (/^Arrow/.test(e.key)) {
                // Collapse to the end the arrow points at, in a box ready to type.
                e.preventDefault();
                e.stopPropagation();
                const list = docFlowList(st);
                const ia = list.findIndex((x) => x.obj.id === sel.anchor.id);
                const ib = list.findIndex((x) => x.obj.id === sel.focus.id);
                const forwards = ia < ib || (ia === ib && sel.anchor.offset <= sel.focus.offset);
                const toEnd = e.key === 'ArrowRight' || e.key === 'ArrowDown';
                const end = (toEnd === forwards) ? sel.focus : sel.anchor;
                st.setDocSelection(null);
                caretAt(end);
                return;
            }
            if (e.key.length === 1 && !e.isComposing) {
                // Typing over the selection replaces it, as in any editor.
                e.preventDefault();
                e.stopPropagation();
                caretAt(st.replaceDocRange(sel, e.key));
            }
        };
        const onCopy = (e) => {
            const st = store();
            const table = !st.docSelection && selectedTable(st, e, { inCell: e.type === 'copy' });
            // Inside one open paragraph a cut stays the browser's own (it has
            // to delete the text too); a copy gets the formatted version.
            const paragraphs = table ? [tableBlock(table.obj)] : st.docSelection
                ? docSelectionParagraphs(st)
                : (e.type === 'copy' ? editorParagraphs(st) : null);
            if (!paragraphs?.length || !e.clipboardData) return;
            const { text, html } = payloadOf(paragraphs);
            e.clipboardData.setData('text/plain', text);
            e.clipboardData.setData('text/html', html);
            e.preventDefault();
            // A table's cut is the keydown's (the event may not come at all).
            if (e.type === 'cut' && !table) caretAt(st.replaceDocRange(st.docSelection, ''));
        };
        const onPaste = (e) => {
            const st = store();
            if (!st.docSelection) return;
            const text = e.clipboardData?.getData('text/plain');
            if (text == null) return;
            e.preventDefault();
            pasteOver(st, text.replace(/\r\n?/g, '\n'));
        };
        window.addEventListener('keydown', onKey, true);
        document.addEventListener('copy', onCopy);
        document.addEventListener('cut', onCopy);
        document.addEventListener('paste', onPaste);
        return () => {
            window.removeEventListener('keydown', onKey, true);
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('cut', onCopy);
            document.removeEventListener('paste', onPaste);
        };
    }, [open, minimized, documentMode]);

    /* ---------------------------------------------------------------- zoom */

    // Ctrl/Cmd + / - / 0 zoom the PAGES, never the browser: inside the editor
    // the browser's own page zoom would only blow up the chrome around them.
    // Capture phase, so a field that stops keydown propagating (the title
    // input) cannot let one through to the browser. Steps from the scale on
    // screen, not the stored zoom, which is stale while a fit mode is on.
    const scaleRef = useRef(scale);
    scaleRef.current = scale;
    useEffect(() => {
        if (!open || minimized) return undefined;
        const onKey = (e) => {
            if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
            // `code` as well as `key`: a non-US layout can report another
            // character for the same physical key.
            const dir = (e.key === '+' || e.key === '=' || e.code === 'Equal' || e.code === 'NumpadAdd') ? 1
                : (e.key === '-' || e.key === '_' || e.code === 'Minus' || e.code === 'NumpadSubtract') ? -1
                    : (e.key === '0' || e.code === 'Digit0' || e.code === 'Numpad0') ? 0
                        : null;
            if (dir === null) return;
            e.preventDefault();
            e.stopPropagation();
            const state = usePdfEditorStore.getState();
            if (!state.ready) return;
            const current = scaleRef.current;
            if (dir === 0) state.setZoom(1);
            else if (dir > 0) state.setZoom(ZOOM_STEPS.find((z) => z > current + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]);
            else state.setZoom([...ZOOM_STEPS].reverse().find((z) => z < current - 0.001) ?? ZOOM_STEPS[0]);
        };
        // A Ctrl/Cmd wheel (or a trackpad pinch) over the ribbon or the side
        // panels would zoom the browser; over the pages the viewport turns it
        // into a page zoom itself.
        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) e.preventDefault();
        };
        window.addEventListener('keydown', onKey, true);
        window.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            window.removeEventListener('keydown', onKey, true);
            window.removeEventListener('wheel', onWheel);
        };
    }, [open, minimized]);

    /* ----------------------------------------------------------- shortcuts */

    useEffect(() => {
        if (!open || minimized) return undefined;
        const onKey = (e) => {
            const tag = e.target?.tagName;
            const typing = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
            const mod = e.ctrlKey || e.metaKey;
            const state = usePdfEditorStore.getState();

            // No document yet: Escape still leaves, nothing else has anything to act on.
            if (!state.ready) {
                if (e.key === 'Escape') requestClose();
                return;
            }

            if (mod && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) state.redo(); else state.undo();
                return;
            }
            if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); state.redo(); return; }
            if (mod && e.key.toLowerCase() === 'f') { e.preventDefault(); state.setSearchOpen(true); return; }
            if (mod && e.key.toLowerCase() === 's') { e.preventDefault(); handleDownload(); return; }
            if (mod && e.key.toLowerCase() === 'p') { e.preventDefault(); handlePrint(); return; }

            // Alignment, on the box being edited or the one selected. These run
            // BEFORE the typing guard on purpose: the whole point is to align
            // the paragraph without leaving the text box to reach the toolbar.
            if (mod && e.shiftKey) {
                const align = { l: 'left', e: 'center', r: 'right', j: 'justify' }[e.key.toLowerCase()];
                const id = state.editingId || state.selectedId;
                if (align && id) {
                    const page = state.pages.find(
                        (p) => (state.objectsByUid[p.uid] || []).some((o) => o.id === id),
                    );
                    const object = page && (state.objectsByUid[page.uid] || []).find((o) => o.id === id);
                    if (object?.type === OBJ.TEXT) {
                        e.preventDefault();
                        state.updateObject(page.uid, id, { align });
                        refocusTextEditor();
                        return;
                    }
                }
            }
            if (typing) return;

            if (e.key === 'Escape') {
                if (state.showSearch) { state.setSearchOpen(false); return; }
                if (state.selectedId) { state.selectObject(null); return; }
                if (state.activeTool !== TOOLS.SELECT) { state.setTool(TOOLS.SELECT); return; }
                requestClose();
                return;
            }
            if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault();
                if (state.selectedId) {
                    const page = state.pages.find(
                        (p) => (state.objectsByUid[p.uid] || []).some((o) => o.id === state.selectedId),
                    );
                    if (page) state.removeObject(page.uid, state.selectedId);
                    return;
                }
                // Nothing selected on the page: the key applies to the page
                // itself, which is what the thumbnail strip is showing as
                // current. `deletePages` refuses to remove the last one.
                const page = state.pages[state.currentPage];
                if (!page || state.pages.length < 2) return;
                const number = state.currentPage + 1;
                state.deletePages([page.uid]);
                notifications.show({
                    title: t('pdfEditor.pages.deletedTitle', { number: toBnDigits(number) }),
                    message: t('pdfEditor.pages.deletedUndo'),
                });
                return;
            }
            if (e.key === '+' || e.key === '=') { state.zoomIn(); return; }
            if (e.key === '-') { state.zoomOut(); return; }
            if (e.key === 'v') state.setTool(TOOLS.SELECT);
            if (e.key === 'h') state.setTool(TOOLS.HAND);
            if (e.key === 't') state.setTool(TOOLS.TEXT);
            if (e.key === 'p') state.setTool(TOOLS.PENCIL);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, minimized, requestClose, t]);

    // Warn before a reload throws away unsaved annotations.
    useEffect(() => {
        if (!open) return undefined;
        const onBeforeUnload = (e) => {
            if (!usePdfEditorStore.getState().dirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [open]);

    const labels = useMemo(() => ({
        rotate: t('pdfEditor.options.rotate'),
        duplicate: t('common.duplicate'),
        delete: t('common.delete'),
        pageNumber: (n) => t('pdfEditor.pageNumber', { number: toBnDigits(n) }),
    }), [t]);

    if (!open) return null;

    return createPortal(
        // Minimised = hidden, not unmounted: `invisible` keeps the layout (so
        // the viewport does not re-fit to a zero-size box) and takes it out of
        // hit-testing and focus, and the store keeps every edit for the restore.
        <div
            className={`fixed inset-0 z-[9000] flex flex-col bg-[#F4F1ED] dark:bg-zinc-950 ${minimized ? 'pointer-events-none invisible' : ''}`}
            aria-hidden={minimized || undefined}
        >
            {/* No title bar: the ribbon's tab row carries the name on the left
                and the document actions and window controls on the right. The
                tools stay in view with no document but are inert until one loads. */}
            <Ribbon
                disabled={awaitingFile}
                leading={awaitingFile ? null : <DocTitle title={title} onTitleChange={setTitle} />}
                trailing={(
                    <DocActions
                        onSearch={() => setSearchOpen(!showSearch)}
                        onPrint={handlePrint}
                        onDownload={handleDownload}
                        onSave={onSave ? handleSave : undefined}
                        onMinimize={onMinimize && !awaitingFile ? handleMinimize : undefined}
                        onClose={requestClose}
                        busy={busy}
                        progress={taskProgress}
                        saving={saving}
                        empty={awaitingFile}
                    />
                )}
                onPickImage={() => imageInputRef.current?.click()}
                onInsertDate={insertDateStamp}
                onInsertTable={insertTable}
                onCoverPage={insertCoverPage}
                onChart={insertChart}
                onScreenshot={insertScreenshot}
                onField={insertField}
                onDropCap={toggleDropCap}
                onCommand={runCommand}
                removingWatermark={removingWatermark}
                onCheckAccessibility={runAccessibilityCheck}
                onColumns={applyColumns}
                onWatermarkImage={() => watermarkImageRef.current?.click()}
                onInsertNote={insertNote}
                onAddBookmark={addBookmarkHere}
            />
            <input
                ref={watermarkImageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { applyWatermarkImage(e.target.files?.[0]); e.target.value = ''; }}
            />
            <input
                ref={replaceImageRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { handleReplaceImageFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            {showSearch ? (
                <SearchBar
                    query={query}
                    onQuery={setQuery}
                    hits={hits}
                    index={hitIndex}
                    running={searchRunning}
                    onStep={stepHit}
                    onClose={() => { setSearchOpen(false); setQuery(''); }}
                />
            ) : null}

            <div className="relative flex min-h-0 flex-1">
                {showThumbnails && ready ? <ThumbnailsPanel /> : null}
                {showBookmarks && ready ? <BookmarksPanel /> : null}

                {awaitingFile ? (
                    <UploadPrompt onFiles={onPickFile} />
                ) : loading || !ready || externalLoading ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3">
                        {error ? (
                            <>
                                <IconAlertTriangle size={30} className="text-[#D33A2C]" />
                                <p className="text-[13.5px] font-medium text-[#D33A2C]">{error}</p>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="rounded-lg border border-[#ECE8E4] px-4 py-2 text-[13px] font-medium text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200"
                                >
                                    {t('common.close')}
                                </button>
                            </>
                        ) : (
                            <>
                                <IconLoader2 size={30} className="animate-spin text-primary" />
                                <p className="text-[13px] text-[#6B635C] dark:text-zinc-400">
                                    {documentMode ? t('pdfEditor.loadingDocument') : t('pdfEditor.loading')}
                                </p>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {displayMode === 'outline' ? (
                            <OutlineView />
                        ) : splitView ? (
                            // Two windows onto one document. The top pane owns
                            // which page the rest of the editor thinks it is on;
                            // the bottom one is free to be somewhere else, which
                            // is the whole point of splitting.
                            <div className="flex min-h-0 flex-1 flex-col">
                                <div className="min-h-0 overflow-hidden" style={{ flexBasis: `${splitRatio * 100}%`, flexGrow: 0 }}>
                                    <Viewport
                                        labels={labels}
                                        searchHitsByPage={hitsByPage}
                                        activeHitId={hits[hitIndex]?.id}
                                        onScaleChange={setScale}
                                        plain={displayMode === 'draft'}
                                        onReplaceImage={startReplaceImage}
                                    />
                                </div>
                                <div
                                    role="separator"
                                    aria-orientation="horizontal"
                                    onPointerDown={(e) => {
                                        e.currentTarget.setPointerCapture(e.pointerId);
                                        splitDragRef.current = e.currentTarget.parentElement;
                                    }}
                                    onPointerMove={(e) => {
                                        const host = splitDragRef.current;
                                        if (!host) return;
                                        const rect = host.getBoundingClientRect();
                                        usePdfEditorStore.getState().setSplitRatio((e.clientY - rect.top) / rect.height);
                                    }}
                                    onPointerUp={() => { splitDragRef.current = null; }}
                                    className="h-2 flex-none cursor-row-resize bg-[#ECE8E4] transition-colors hover:bg-primary/40 dark:bg-zinc-800"
                                />
                                <div className="min-h-0 flex-1 overflow-hidden">
                                    <Viewport
                                        labels={labels}
                                        searchHitsByPage={hitsByPage}
                                        activeHitId={hits[hitIndex]?.id}
                                        syncScroll={false}
                                        plain={displayMode === 'draft'}
                                        onReplaceImage={startReplaceImage}
                                    />
                                </div>
                            </div>
                        ) : (
                            <Viewport
                                labels={labels}
                                searchHitsByPage={hitsByPage}
                                activeHitId={hits[hitIndex]?.id}
                                onScaleChange={setScale}
                                plain={displayMode === 'draft'}
                                onReplaceImage={startReplaceImage}
                            />
                        )}
                        {showReviewing && !compact ? <ReviewingPanel /> : null}
                        {showAccessibility && !compact ? <AccessibilityPanel /> : null}
                        {showInspector && !compact ? <InspectorPanel /> : null}
                        <FloatingPageBar scale={scale} />
                    </>
                )}
            </div>

            {ready ? <StatusBar scale={scale} /> : null}

            <input
                ref={imageInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { handleImageFile(e.target.files?.[0]); e.target.value = ''; }}
            />

            {pagesModal ? <PagesModal onClose={() => setPagesModal(false)} /> : null}
            {symbolModal ? (
                <SymbolModal
                    fontCss={usePdfEditorStore.getState().toolSettings.text?.fontStack}
                    onClose={() => setSymbolModal(false)}
                    onApply={insertSymbol}
                />
            ) : null}
            {equationModal ? (
                <EquationModal
                    onClose={() => setEquationModal(false)}
                    onApply={({ src, width, height }) => placeImage(src, width, height, 'equation')}
                />
            ) : null}
            {emojiModal ? (
                <EmojiModal
                    onClose={() => setEmojiModal(false)}
                    onApply={({ src, width, height }) => placeImage(src, width, height, 'emoji')}
                />
            ) : null}
            {signModal ? (
                <SignModal
                    onClose={() => setSignModal(false)}
                    onApply={({ src, width, height }) => placeImage(src, width, height, 'sign')}
                />
            ) : null}
            {linkDraft ? (
                <LinkModal
                    object={(objectsByUid[linkDraft.uid] || []).find((o) => o.id === linkDraft.id)}
                    pageCount={pages.length}
                    isNew={linkDraft.isNew}
                    onApply={(patch) => {
                        usePdfEditorStore.getState().updateObject(linkDraft.uid, linkDraft.id, patch);
                    }}
                    onRemove={() => {
                        usePdfEditorStore.getState().removeObject(linkDraft.uid, linkDraft.id);
                    }}
                    onClose={() => setLinkDraft(null)}
                />
            ) : null}
        </div>,
        document.body,
    );
}
